import type { ApiClient } from '@nly/api-client'
import type { DoctorProfileInput } from '@/views/ecosystem_entities/physician_alliances/model'
import { apiClient } from '@/api'
import { formatApiError } from '@/utils/apiError'

type DoctorMinimalCreateInput = Parameters<ApiClient['doctor']['createMinimal']>[0]

export const useDoctorStore = defineStore('doctor', () => {
  const listResult = ref<Awaited<ReturnType<typeof apiClient.doctor.list>> | null>(null)
  const currentDoctor = ref<Awaited<ReturnType<typeof apiClient.doctor.get>> | null>(null)
  const communicationLogs = ref<Awaited<ReturnType<typeof apiClient.doctor.communication.list>>>([])
  const listLoading = ref(false)
  const detailLoading = ref(false)
  const mutationLoading = ref(false)
  const listError = ref<string | null>(null)
  const detailError = ref<string | null>(null)
  let listGeneration = 0

  async function loadList(input: Parameters<typeof apiClient.doctor.list>[0]) {
    const request = ++listGeneration
    listLoading.value = true
    listError.value = null
    try {
      const result = await apiClient.doctor.list(input)
      if (request !== listGeneration) {
        return result
      }
      listResult.value = result
      return result
    }
    catch (error) {
      if (request === listGeneration) {
        listError.value = formatApiError(error, '医师列表加载失败')
      }
      throw error
    }
    finally {
      if (request === listGeneration) {
        listLoading.value = false
      }
    }
  }

  async function loadDetail(id: number) {
    detailLoading.value = true
    detailError.value = null
    try {
      const [doctor, logs] = await Promise.all([
        apiClient.doctor.get({ id }),
        apiClient.doctor.communication.list({ doctorId: id }),
      ])
      currentDoctor.value = doctor
      communicationLogs.value = logs
      return doctor
    }
    catch (error) {
      detailError.value = formatApiError(error, '医师详情加载失败')
      throw error
    }
    finally {
      detailLoading.value = false
    }
  }

  async function createDoctor(profile: DoctorProfileInput) {
    mutationLoading.value = true
    try {
      return await apiClient.doctor.create(profile)
    }
    finally {
      mutationLoading.value = false
    }
  }

  async function createMinimalDoctor(input: DoctorMinimalCreateInput) {
    mutationLoading.value = true
    try {
      return await apiClient.doctor.createMinimal(input)
    }
    finally {
      mutationLoading.value = false
    }
  }

  async function updateDoctor(id: number, profile: DoctorProfileInput) {
    mutationLoading.value = true
    try {
      const doctor = await apiClient.doctor.update({ id, profile })
      currentDoctor.value = doctor
      return doctor
    }
    finally {
      mutationLoading.value = false
    }
  }

  async function addCommunication(input: Parameters<typeof apiClient.doctor.communication.create>[0]) {
    mutationLoading.value = true
    try {
      const log = await apiClient.doctor.communication.create(input)
      communicationLogs.value = [log, ...communicationLogs.value]
      return log
    }
    finally {
      mutationLoading.value = false
    }
  }

  async function reviewCommunication(input: Parameters<typeof apiClient.doctor.communication.review>[0]) {
    mutationLoading.value = true
    try {
      const result = await apiClient.doctor.communication.review(input)
      currentDoctor.value = result.doctor
      communicationLogs.value = communicationLogs.value.map(log => (
        log.id === result.log.id ? result.log : log
      ))
      return result
    }
    finally {
      mutationLoading.value = false
    }
  }

  function clearDetail() {
    currentDoctor.value = null
    communicationLogs.value = []
    detailError.value = null
  }

  function reset() {
    listGeneration++
    listResult.value = null
    currentDoctor.value = null
    communicationLogs.value = []
    listLoading.value = false
    detailLoading.value = false
    mutationLoading.value = false
    listError.value = null
    detailError.value = null
  }

  return {
    listResult,
    currentDoctor,
    communicationLogs,
    listLoading,
    detailLoading,
    mutationLoading,
    listError,
    detailError,
    loadList,
    loadDetail,
    createDoctor,
    createMinimalDoctor,
    updateDoctor,
    addCommunication,
    reviewCommunication,
    clearDetail,
    reset,
  }
})
